import React, { useState, useEffect } from "react";

const API = process.env.REACT_APP_API_URL || "";

const FACTOR_LABELS = {
  fcf_yield_score:   "FCF Yield",
  bm_score:          "Book/Mkt",
  tev_score:         "Size (TEV)",
  roa_score:         "ROA",
  price_range_score: "Entry Point",
  momentum_score:    "Momentum",
};

const scoreColor = (v) => {
  if (v === null || v === undefined) return "#888";
  if (v >= 70) return "#1D9E75";
  if (v >= 40) return "#BA7517";
  return "#E24B4A";
};

const Bar = ({ value, max = 100 }) => (
  <div style={{ background: "#1a1a2e", borderRadius: 4, height: 6, width: "100%", overflow: "hidden" }}>
    <div style={{
      height: "100%", borderRadius: 4,
      width: `${Math.min(100, (value / max) * 100)}%`,
      background: scoreColor(value),
      transition: "width 0.4s ease",
    }} />
  </div>
);

// Table column definitions: [header, sortKey]
const TABLE_COLS = [
  ["Ticker",      "ticker"],
  ["List",        "list"],
  ["Company",     "company_name"],
  ["Theme",       "sector"],
  ["Score",       "final_score"],
  ["Price",       "price"],
  ["Buy Zone",    "buyzone"],
  ["vs SMA",      "price_vs_sma_200"],
  ["FCF Yield",   "fcf_yield_score"],
  ["B/M",         "bm_score"],
  ["ROA",         "roa_score"],
  ["Entry Pt",    "price_range_score"],
  ["Momentum",    "momentum_score"],
  ["Inv",         "inv_dummy"],
  ["CORR\u00d7",  "corr004_multiplier"],
];

export default function SignalsDashboard() {
  const [scores, setScores]                 = useState([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);
  const [selected, setSelected]             = useState(null);
  const [cardSortKey, setCardSortKey]       = useState("final_score");
  const [view, setView]                     = useState("cards");
  const [running, setRunning]               = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [profileCache, setProfileCache]     = useState({});  // ticker -> { description, ... }

  // Table sort state
  const [sortCol, setSortCol]   = useState("final_score");
  const [sortDir, setSortDir]   = useState("desc");
  const [expandedTicker, setExpandedTicker] = useState(null);

  useEffect(() => {
    fetch(`${API}/signals/scores`)
      .then((r) => r.json())
      .then((data) => { setScores(data); setLoading(false); })
      .catch((e) => { setError(e.message); setLoading(false); });
  }, []);

  // Card view sort (existing buttons)
  const cardSorted = [...scores].sort((a, b) => (b[cardSortKey] ?? -1) - (a[cardSortKey] ?? -1));

  // Table view sort (column header click)
  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortCol(col);
      setSortDir("desc");
    }
  };

  const getSortIcon = (col) => {
    if (sortCol !== col) return " \u2195";
    return sortDir === "asc" ? " \u2191" : " \u2193";
  };

  const tableSorted = [...scores].sort((a, b) => {
    let aVal = a[sortCol];
    let bVal = b[sortCol];
    if (aVal == null) aVal = sortDir === "asc" ? Infinity : -Infinity;
    if (bVal == null) bVal = sortDir === "asc" ? Infinity : -Infinity;
    if (typeof aVal === "string" && typeof bVal === "string") {
      return sortDir === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    if (typeof aVal === "boolean") {
      return sortDir === "asc" ? (aVal ? 1 : 0) - (bVal ? 1 : 0) : (bVal ? 1 : 0) - (aVal ? 1 : 0);
    }
    const aNum = typeof aVal === "string" ? parseFloat(aVal) || 0 : aVal;
    const bNum = typeof bVal === "string" ? parseFloat(bVal) || 0 : bVal;
    return sortDir === "asc" ? aNum - bNum : bNum - aNum;
  });

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await fetch(`${API}/admin/signals/run`, {
        method: "POST",
      });
      if (!r.ok) throw new Error(`${r.status}`);
      const data = await fetch(`${API}/signals/scores`).then(res => res.json());
      setScores(data);
    } catch (e) {
      alert(`Run failed: ${e.message}`);
    } finally {
      setRunning(false);
    }
  };

  const downloadPDF = async () => {
    setDownloadingPDF(true);
    try {
      console.log("[PDF] fetching from:", API + "/signals/pdf");
      const resp = await fetch(`${API}/signals/pdf`);
      console.log("[PDF] response status:", resp.status,
                  "content-type:", resp.headers.get("content-type"));
      if (!resp.ok) {
        const errText = await resp.text();
        console.error("[PDF] error body:", errText);
        throw new Error(`PDF fetch failed: ${resp.status}`);
      }
      const blob = await resp.blob();
      console.log("[PDF] blob size:", blob.size, "type:", blob.type);
      if (blob.size === 0) throw new Error("Empty PDF blob received");
      const url = window.URL.createObjectURL(
        new Blob([blob], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `axiom_signals_${new Date().toISOString().split("T")[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("[PDF] download failed:", err);
      alert(`PDF download failed: ${err.message}. Check browser console.`);
    } finally {
      setDownloadingPDF(false);
    }
  };

  const exportCSV = () => {
    const headers = [
      "Ticker", "List", "Company", "Theme", "Score", "Price", "Buy Zone",
      "vs SMA", "FCF Yield", "B/M", "ROA", "Entry Pt%", "Momentum%",
      "Inv Dummy", "CORR x", "Factor Score", "Flags",
    ];
    const rows = tableSorted.map(r => [
      r.ticker ?? "",
      r.list ?? "search",
      r.company_name ?? "",
      r.sector ?? "",
      r.final_score ?? "",
      r.price ?? "",
      r.buyzone > 1.0 ? r.buyzone : "",
      r.price_vs_sma_200 ?? "",
      r.fcf_yield != null ? (r.fcf_yield * 100).toFixed(2) : "",
      r.book_to_market?.toFixed(3) ?? "",
      r.roa != null ? (r.roa * 100).toFixed(2) : "",
      r.price_range_pct?.toFixed(1) ?? "",
      r.momentum_6m_pct?.toFixed(1) ?? "",
      r.inv_dummy === true ? "Caution" : r.inv_dummy === false ? "OK" : "",
      r.corr004_multiplier?.toFixed(3) ?? "",
      r.factor_score ?? "",
      r.flag_notes ?? "",
    ]);
    const csv = [headers, ...rows]
      .map(r => r.map(v => typeof v === "string" && v.includes(",") ? `"${v}"` : v).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `axiom_signals_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleExpand = (ticker) => {
    setExpandedTicker(t => t === ticker ? null : ticker);
    // Fetch profile on expand if not cached
    if (expandedTicker !== ticker && !profileCache[ticker]) {
      fetch(`${API}/signals/profile/${ticker}`)
        .then(r => r.json())
        .then(data => setProfileCache(prev => ({ ...prev, [ticker]: data })))
        .catch(() => {});
    }
  };

  if (loading) return (
    <div style={{ padding: 48, color: "#9a99a2", fontFamily: "monospace", textAlign: "center" }}>
      Loading signal scores...
    </div>
  );
  if (error) return (
    <div style={{ padding: 48, color: "#E24B4A", fontFamily: "monospace" }}>
      Error: {error}
    </div>
  );

  const thStyle = {
    padding: "10px 12px", textAlign: "left", fontSize: 10, fontWeight: 600,
    color: "#5f5e6e", textTransform: "uppercase", letterSpacing: 1, whiteSpace: "nowrap",
    cursor: "pointer", userSelect: "none",
  };

  return (
    <div style={{ minHeight: "100vh", background: "#0d0d1a", color: "#e2e0d8", fontFamily: "'Inter', sans-serif", padding: "24px 32px" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 11, color: "#5f5e6e", letterSpacing: 2, textTransform: "uppercase", marginBottom: 4 }}>
            Axiom Intelligence
          </div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: "#e8e6de" }}>
            Trading Signals
          </h1>
          <div style={{ fontSize: 12, color: "#5f5e6e", marginTop: 4 }}>
            Multibagger factor model - CORR-004 overlay - {scores.length} tickers
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <a href="/signals/raw" style={{
            background: "#1a1a2e", color: "#9a99a2", padding: "8px 16px",
            borderRadius: 6, fontSize: 12, textDecoration: "none", border: "1px solid #2a2a3e",
          }}>Raw Data</a>
          <a href="/signals/raw?flagged=true" style={{
            background: "#2a1a1a", color: "#E24B4A", padding: "8px 16px",
            borderRadius: 6, fontSize: 12, textDecoration: "none", border: "1px solid #E24B4A44",
          }}>Flagged</a>
          <button onClick={runNow} disabled={running} style={{
            background: running ? "#2a2a3e" : "#1D9E75", color: "#fff", padding: "8px 16px",
            borderRadius: 6, fontSize: 12, fontWeight: 500, border: "none",
            cursor: running ? "wait" : "pointer", opacity: running ? 0.7 : 1,
          }}>{running ? "Running..." : "Run Now"}</button>
          {view === "table" && (
            <button onClick={exportCSV} style={{
              background: "#1a1a2e", color: "#9a99a2", padding: "8px 16px",
              borderRadius: 6, fontSize: 12, border: "1px solid #2a2a3e", cursor: "pointer",
            }}>Export CSV</button>
          )}
          <button onClick={downloadPDF} disabled={downloadingPDF} style={{
            background: downloadingPDF ? "#2a2a3e" : "#185FA5", color: "#fff", padding: "8px 16px",
            borderRadius: 6, fontSize: 12, fontWeight: 500, border: "none",
            cursor: downloadingPDF ? "wait" : "pointer", opacity: downloadingPDF ? 0.7 : 1,
          }}>{downloadingPDF ? "Generating..." : "Download PDF"}</button>
        </div>
      </div>

      {/* Sort controls + view toggle */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        {view === "cards" && ["final_score", "factor_score", "fcf_yield_score", "bm_score", "price_range_score"].map((k) => (
          <button key={k} onClick={() => setCardSortKey(k)} style={{
            padding: "4px 12px", borderRadius: 4, fontSize: 11, cursor: "pointer",
            border: cardSortKey === k ? "1px solid #185FA5" : "1px solid #2a2a3e",
            background: cardSortKey === k ? "#185FA5" : "#1a1a2e",
            color: cardSortKey === k ? "#fff" : "#9a99a2",
          }}>{k.replace(/_score|_/g, " ").trim()}</button>
        ))}
        <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
          {["cards", "table"].map((v) => (
            <button key={v} onClick={() => setView(v)} style={{
              padding: "4px 12px", borderRadius: 4, fontSize: 11, cursor: "pointer",
              border: view === v ? "1px solid #185FA5" : "1px solid #2a2a3e",
              background: view === v ? "#185FA5" : "#1a1a2e",
              color: view === v ? "#fff" : "#9a99a2", textTransform: "capitalize",
            }}>{v}</button>
          ))}
        </div>
      </div>

      {/* Table view */}
      {view === "table" && tableSorted.length > 0 && (
        <div style={{ borderRadius: 12, overflow: "auto", border: "1px solid #2a2a3e", marginBottom: 16, maxHeight: "calc(100vh - 200px)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#1a1a2e", borderBottom: "1px solid #2a2a3e" }}>
                {TABLE_COLS.map(([h, key]) => (
                  <th key={h} onClick={() => handleSort(key)} style={thStyle}>
                    {h}{getSortIcon(key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableSorted.map((row, i) => {
                const isExpanded = expandedTicker === row.ticker;
                const inBuyzone = row.buyzone > 1.0 && row.price != null && row.price <= row.buyzone;
                const deepBuyzone = inBuyzone && row.price <= row.buyzone * 0.85;
                const nearBuyzone = !inBuyzone && row.buyzone > 1.0 && row.price != null && row.price <= row.buyzone * 1.10;
                const baseBg = i % 2 === 0 ? "#12121f" : "#151525";
                const rowBg = deepBuyzone ? "#2a0a0a" : inBuyzone ? "#0a2a1a" : nearBuyzone ? "#2a2a0a" : baseBg;
                return (
                  <React.Fragment key={row.ticker}>
                    <tr
                      style={{
                        background: rowBg,
                        borderBottom: "1px solid #1e1e30",
                        borderLeft: deepBuyzone ? "3px solid #E24B4A" : inBuyzone ? "3px solid #1D9E75" : nearBuyzone ? "3px solid #C9A227" : "3px solid transparent",
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "#1a1a2e"}
                      onMouseLeave={(e) => e.currentTarget.style.background = rowBg}
                    >
                      <td style={{ padding: "8px 12px", fontWeight: 600, color: deepBuyzone ? "#E24B4A" : inBuyzone ? "#1D9E75" : nearBuyzone ? "#C9A227" : "#e8e6de" }}
                          onClick={() => toggleExpand(row.ticker)}>
                        {isExpanded ? "\u25BC" : "\u25B6"} {row.ticker}
                      </td>
                      <td style={{ padding: "8px 12px" }}>
                        <span style={{ fontSize: 10, color: row.list === "watch" ? "#BA7517" : row.list === "long_invest" ? "#7B8CDE" : "#1D9E75", background: row.list === "watch" ? "#2a2a0a" : row.list === "long_invest" ? "#1a1a2e" : "#0a2a1a", padding: "2px 6px", borderRadius: 3, textTransform: "uppercase", fontWeight: 600 }}>{row.list === "long_invest" ? "LONG" : (row.list || "search")}</span>
                      </td>
                      <td style={{ padding: "8px 12px", color: "#9a99a2", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.company_name}</td>
                      <td style={{ padding: "8px 12px" }}>
                        <span style={{ fontSize: 10, color: "#9a99a2", background: "#1e1e30", padding: "2px 6px", borderRadius: 3 }}>{row.sector}</span>
                      </td>
                      <td style={{ padding: "8px 12px", fontWeight: 700, color: scoreColor(row.final_score) }}>
                        {row.final_score != null ? Math.round(row.final_score) : "-"}
                      </td>
                      <td style={{ padding: "8px 12px", color: deepBuyzone ? "#E24B4A" : inBuyzone ? "#1D9E75" : nearBuyzone ? "#C9A227" : "#e2e0d8", fontWeight: (inBuyzone || nearBuyzone) ? 700 : 400 }}>
                        {row.price != null ? `$${row.price.toFixed(2)}` : "-"}
                      </td>
                      <td style={{ padding: "8px 12px", color: deepBuyzone ? "#E24B4A" : inBuyzone ? "#1D9E75" : nearBuyzone ? "#C9A227" : "#9a99a2" }}>
                        {row.buyzone > 1.0 ? `$${row.buyzone.toFixed(2)}` : "\u2014"}
                      </td>
                      <td style={{ padding: "8px 12px", color: row.price_vs_sma_200 === "below" ? "#1D9E75" : "#9a99a2", fontWeight: 600 }}>
                        {row.price_vs_sma_200 === "below" ? "\u25BC Below" : row.price_vs_sma_200 === "above" ? "\u25B2 Above" : "\u2014"}
                      </td>
                      <td style={{ padding: "8px 12px", color: scoreColor(row.fcf_yield_score) }}>
                        {row.fcf_yield != null ? (row.fcf_yield * 100).toFixed(1) + "%" : "-"}
                      </td>
                      <td style={{ padding: "8px 12px", color: "#e2e0d8" }}>{row.book_to_market?.toFixed(3) ?? "-"}</td>
                      <td style={{ padding: "8px 12px", color: "#e2e0d8" }}>{row.roa != null ? (row.roa * 100).toFixed(1) + "%" : "-"}</td>
                      <td style={{ padding: "8px 12px", color: scoreColor(row.price_range_score) }}>
                        {row.price_range_pct != null ? row.price_range_pct.toFixed(1) + "%" : "-"}
                      </td>
                      <td style={{ padding: "8px 12px", color: scoreColor(row.momentum_score) }}>
                        {row.momentum_6m_pct != null ? row.momentum_6m_pct.toFixed(1) + "%" : "-"}
                      </td>
                      <td style={{ padding: "8px 12px" }}>
                        <span style={{ color: row.inv_dummy === true ? "#E24B4A" : row.inv_dummy === false ? "#1D9E75" : "#5f5e6e", fontWeight: 600 }}>
                          {row.inv_dummy === true ? "!" : row.inv_dummy === false ? "\u2713" : "-"}
                        </span>
                      </td>
                      <td style={{ padding: "8px 12px", color: "#e2e0d8" }}>{row.corr004_multiplier?.toFixed(3) ?? "-"}</td>
                    </tr>

                    {/* Expanded detail row */}
                    {isExpanded && (
                      <tr style={{ background: "#0d0d18" }}>
                        <td colSpan={TABLE_COLS.length} style={{ padding: "12px 16px" }}>
                          {/* Company description */}
                          {(() => {
                            const profile = profileCache[row.ticker];
                            return (
                              <div style={{ marginBottom: 12, padding: "10px 12px", background: "#111122", borderRadius: 8, border: "1px solid #1e1e30" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                                  <span style={{ fontSize: 13, fontWeight: 700, color: "#e8e6de" }}>{profile?.companyName || row.company_name || row.ticker}</span>
                                  {profile?.industry && <span style={{ fontSize: 10, color: "#5f5e6e", background: "#1a1a2e", padding: "1px 6px", borderRadius: 3 }}>{profile.industry}</span>}
                                  {profile?.exchange && <span style={{ fontSize: 10, color: "#5f5e6e" }}>{profile.exchange}</span>}
                                </div>
                                <div style={{ fontSize: 11, color: "#9a99a2", lineHeight: 1.5 }}>
                                  {profile?.description
                                    ? profile.description.length > 400
                                      ? profile.description.substring(0, 400) + "..."
                                      : profile.description
                                    : <span style={{ color: "#5f5e6e", fontStyle: "italic" }}>Loading description...</span>
                                  }
                                </div>
                              </div>
                            );
                          })()}
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 16, fontSize: 11 }}>

                            {/* Factor Scores */}
                            <div>
                              <div style={{ color: "#5f5e6e", fontSize: 10, textTransform: "uppercase", marginBottom: 6, letterSpacing: 1 }}>Factor Scores</div>
                              <div style={{ color: "#e2e0d8" }}>FCF Yield: {row.fcf_yield_score ?? "\u2014"}</div>
                              <div style={{ color: "#e2e0d8" }}>Book/Market: {row.bm_score ?? "\u2014"}</div>
                              <div style={{ color: "#e2e0d8" }}>Size (TEV): {row.tev_score ?? "\u2014"}</div>
                              <div style={{ color: "#e2e0d8" }}>ROA: {row.roa_score ?? "\u2014"}</div>
                              <div style={{ color: "#e2e0d8" }}>Price Range: {row.price_range_score ?? "\u2014"}</div>
                              <div style={{ color: "#e2e0d8" }}>6m Momentum: {row.momentum_score ?? "\u2014"}</div>
                            </div>

                            {/* Raw Values */}
                            <div>
                              <div style={{ color: "#5f5e6e", fontSize: 10, textTransform: "uppercase", marginBottom: 6, letterSpacing: 1 }}>Raw Data</div>
                              <div style={{ color: "#e2e0d8" }}>Market Cap: {row.market_cap ? `$${(row.market_cap / 1e9).toFixed(1)}B` : "\u2014"}</div>
                              <div style={{ color: "#e2e0d8" }}>FCF Yield: {row.fcf_yield != null ? `${(row.fcf_yield * 100).toFixed(2)}%` : "\u2014"}</div>
                              <div style={{ color: "#e2e0d8" }}>200 SMA: {row.sma_200 ? `$${row.sma_200.toFixed(2)}` : "\u2014"}</div>
                              <div style={{ color: "#e2e0d8" }}>Buy Zone: {row.buyzone > 1.0 ? `$${row.buyzone.toFixed(2)}` : "\u2014"}</div>
                              <div style={{ color: "#e2e0d8" }}>52wk Range: {row.year_low?.toFixed(2) ?? "?"} - {row.year_high?.toFixed(2) ?? "?"}</div>
                            </div>

                            {/* CORR-004 */}
                            <div>
                              <div style={{ color: "#5f5e6e", fontSize: 10, textTransform: "uppercase", marginBottom: 6, letterSpacing: 1 }}>CORR-004 Overlay</div>
                              <div style={{ color: "#e2e0d8" }}>Theme: {row.sector ?? "\u2014"}</div>
                              <div style={{ color: "#e2e0d8" }}>Multiplier: {row.corr004_multiplier ? `${row.corr004_multiplier.toFixed(3)}x` : "\u2014"}</div>
                              <div style={{ color: "#e2e0d8" }}>Factor Score: {row.factor_score ?? "\u2014"}</div>
                              <div style={{ color: scoreColor(row.final_score), fontWeight: 700 }}>Final Score: {row.final_score ?? "\u2014"}</div>
                            </div>

                            {/* IV&V */}
                            <div>
                              <div style={{ color: "#5f5e6e", fontSize: 10, textTransform: "uppercase", marginBottom: 6, letterSpacing: 1 }}>IV&V Status</div>
                              <div style={{ color: row.flag_notes ? "#BA7517" : "#1D9E75", fontWeight: 600 }}>
                                {row.flag_notes || "Clean"}
                              </div>
                              {row.ivv_notes && (
                                <div style={{ color: "#9a99a2", fontSize: 10, marginTop: 4 }}>{row.ivv_notes}</div>
                              )}
                              <div style={{ color: "#5f5e6e", fontSize: 10, marginTop: 6 }}>Run: {row.run_date}</div>
                            </div>

                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Ticker grid (cards view) */}
      {view === "cards" && <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
        {cardSorted.map((row) => (
          <div
            key={row.ticker}
            onClick={() => setSelected(selected?.ticker === row.ticker ? null : row)}
            style={{
              background: selected?.ticker === row.ticker ? "#1a1a2e" : "#12121f",
              border: `1px solid ${selected?.ticker === row.ticker ? "#185FA5" : "#2a2a3e"}`,
              borderRadius: 10, padding: "14px 16px", cursor: "pointer",
              transition: "border-color 0.2s",
            }}
          >
            {/* Top row */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: "#e8e6de" }}>{row.ticker}</div>
                <div style={{ fontSize: 11, color: "#5f5e6e", marginTop: 2 }}>{row.company_name}</div>
                <div style={{
                  fontSize: 10, color: "#9a99a2", background: "#1e1e30",
                  padding: "2px 6px", borderRadius: 3, display: "inline-block", marginTop: 4,
                }}>{row.sector}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: scoreColor(row.final_score) }}>
                  {row.final_score != null ? Math.round(row.final_score) : "-"}
                </div>
                <div style={{ fontSize: 10, color: "#5f5e6e" }}>final score</div>
              </div>
            </div>

            {/* Factor bars */}
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              {Object.entries(FACTOR_LABELS).map(([key, label]) => (
                <div key={key}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#5f5e6e", marginBottom: 2 }}>
                    <span>{label}</span>
                    <span style={{ color: scoreColor(row[key]) }}>
                      {row[key] != null ? Math.round(row[key]) : "-"}
                    </span>
                  </div>
                  <Bar value={row[key] ?? 0} />
                </div>
              ))}
            </div>

            {/* Expanded detail */}
            {selected?.ticker === row.ticker && (
              <div style={{ marginTop: 12, borderTop: "1px solid #2a2a3e", paddingTop: 10, fontSize: 11, color: "#9a99a2" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 12px" }}>
                  <span>FCF Yield</span>     <span style={{ color: "#e2e0d8" }}>{row.fcf_yield != null ? (row.fcf_yield * 100).toFixed(2) + "%" : "-"}</span>
                  <span>Book/Mkt</span>       <span style={{ color: "#e2e0d8" }}>{row.book_to_market?.toFixed(3) ?? "-"}</span>
                  <span>ROA</span>             <span style={{ color: "#e2e0d8" }}>{row.roa != null ? (row.roa * 100).toFixed(2) + "%" : "-"}</span>
                  <span>Price Range</span>    <span style={{ color: "#e2e0d8" }}>{row.price_range_pct?.toFixed(1) + "%" ?? "-"}</span>
                  <span>6m Momentum</span>    <span style={{ color: scoreColor(100 - (row.momentum_6m_pct ?? 50)) }}>{row.momentum_6m_pct?.toFixed(1) + "%" ?? "-"}</span>
                  <span>Inv. Signal</span>    <span style={{ color: row.inv_dummy ? "#E24B4A" : "#1D9E75" }}>{row.inv_dummy === true ? "Caution" : row.inv_dummy === false ? "OK" : "-"}</span>
                  <span>CORR-004 x</span>     <span style={{ color: "#e2e0d8" }}>{row.corr004_multiplier?.toFixed(3) ?? "-"}</span>
                  <span>Run date</span>        <span style={{ color: "#e2e0d8" }}>{row.run_date}</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>}

      {/* Empty state */}
      {scores.length === 0 && (
        <div style={{ textAlign: "center", color: "#5f5e6e", marginTop: 80, fontSize: 14 }}>
          No scores yet. Trigger a manual run via POST /admin/signals/run or wait for next scheduled run.
        </div>
      )}

    </div>
  );
}
